import { useEffect, useRef, useState } from "react";

/**
 * RotatingVideo
 * ------------------------------------------------------------
 * Cycles through an array of {src, poster} videos with a smooth
 * cross-fade transition. Muted autoplay, loop, playsinline.
 * Lazy-mounts once the element enters the viewport. Poster shows
 * as fallback while loading or if the video is unavailable.
 *
 * Props:
 *  - videos: [{src, poster}]
 *  - interval: ms per clip (default 9000)
 *  - fade: transition duration (default 1200)
 *  - className: applied to wrapper
 *  - overlay: react node rendered above the video
 *  - eager: skip lazy-load (for above-the-fold hero)
 *  - testId: data-testid attribute
 */
export default function RotatingVideo({
    videos,
    interval = 9000,
    fade = 1200,
    className = "",
    overlay = null,
    eager = false,
    testId = "rotating-video",
    kenBurns = true,
}) {
    const wrapRef = useRef(null);
    const [mounted, setMounted] = useState(eager);
    const [active, setActive] = useState(0);
    const timerRef = useRef(null);

    useEffect(() => {
        if (mounted || !wrapRef.current) return;
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => e.isIntersecting && setMounted(true)),
            { rootMargin: "300px" }
        );
        io.observe(wrapRef.current);
        return () => io.disconnect();
    }, [mounted]);

    useEffect(() => {
        if (!mounted || videos.length <= 1) return;
        timerRef.current = setInterval(() => {
            setActive((i) => (i + 1) % videos.length);
        }, interval);
        return () => clearInterval(timerRef.current);
    }, [mounted, interval, videos.length]);

    return (
        <div
            ref={wrapRef}
            data-testid={testId}
            className={`overflow-hidden ${className || "relative"}`}
        >
            {/* base poster to guarantee a visible layer immediately */}
            <div
                aria-hidden="true"
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url('${videos[0].poster}')` }}
            />
            {mounted &&
                videos.map((v, i) => (
                    <video
                        key={v.src}
                        data-testid={`${testId}-${i}`}
                        aria-hidden="true"
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload={i === 0 ? "auto" : "metadata"}
                        poster={v.poster}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out ${
                            kenBurns ? "animate-ken-burns" : ""
                        }`}
                        style={{
                            transitionDuration: `${fade}ms`,
                            opacity: i === active ? 1 : 0,
                        }}
                    >
                        <source src={v.src} type="video/mp4" />
                    </video>
                ))}
            {overlay}
        </div>
    );
}
